// client/src/services/maintenanceService.ts
import apiClient from './api';

// Types locaux pour éviter les conflits
interface MaintenancePaginatedResponse {
  maintenance: MaintenanceRecord[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface MaintenanceRecord {
  id: string;
  vehicle_id: string;
  vehicle_plate: string;
  vehicle_brand?: string;
  vehicle_model?: string;
  date: string;
  type: 'routine' | 'repair' | 'inspection' | 'emergency';
  description: string;
  cost?: number;
  mileage?: number;
  next_maintenance_date?: string;
  performed_by?: string;
  performed_by_first_name?: string;
  performed_by_last_name?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface MaintenanceFilters {
  page?: number;
  limit?: number;
  search?: string;
  type?: string;
  vehicle_id?: string;
  date_from?: string;
  date_to?: string;
}

export interface MaintenanceStats {
  total_maintenance: number;
  routine_count: number;
  repair_count: number;
  inspection_count: number;
  emergency_count: number;
  total_cost: number;
  average_cost: number;
  last_30_days_count: number;
  last_30_days_cost: number;
}

export interface CreateMaintenanceData {
  vehicle_id: string;
  date: string;
  type: 'routine' | 'repair' | 'inspection' | 'emergency';
  description: string;
  cost?: number;
  mileage?: number;
  next_maintenance_date?: string;
  notes?: string;
}

export interface UpdateMaintenanceData extends Partial<CreateMaintenanceData> {}

/**
 * Service pour la gestion de la maintenance
 */
export class MaintenanceService {
  private readonly baseUrl = '/admin/maintenance';

  /**
   * Récupérer la liste des maintenances avec filtres et pagination
   */
  async getMaintenances(filters: MaintenanceFilters = {}): Promise<MaintenancePaginatedResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
    
    const url = params.toString() ? `${this.baseUrl}?${params}` : this.baseUrl;
    const response = await apiClient.get(url);
    return response.data;
  }

  /**
   * Récupérer une maintenance par son ID
   */
  async getMaintenance(id: string): Promise<MaintenanceRecord> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Créer une nouvelle maintenance
   */
  async createMaintenance(data: CreateMaintenanceData): Promise<MaintenanceRecord> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data;
  }

  /**
   * Mettre à jour une maintenance
   */
  async updateMaintenance(id: string, data: UpdateMaintenanceData): Promise<MaintenanceRecord> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  /**
   * Supprimer une maintenance
   */
  async deleteMaintenance(id: string): Promise<{ message: string }> {
    const response = await apiClient.delete(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Récupérer les statistiques de maintenance
   */
  async getMaintenanceStats(): Promise<MaintenanceStats> {
    const response = await apiClient.get(`${this.baseUrl}/stats`);
    return response.data;
  }
}

// Export d'une instance singleton
export const maintenanceService = new MaintenanceService();